import { useState, useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { setAuthToken, getSetting, updateSetting } from '../services/api';
import { toast } from 'react-toastify';
import {
  Radio, Download, History, Loader2,
  CheckCircle, XCircle, RefreshCw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Wifi, WifiOff, Eye, EyeOff, ArrowUpRight, ArrowDownLeft, X,
  Trash2, FileDown, Image, FolderPlus, Upload, MoreVertical,
  Folder, File, ArrowLeft, Edit3, Move, Copy, FolderOpen,
  ExternalLink, Key, HelpCircle,
} from 'lucide-react';
import Loading from '../components/Loading';
import { usePerm } from '../lib/perms';
import './Parametres.css';

import api, { API_BASE_URL } from '../services/api';
import {
  mediaList, mediaCreateFolder, mediaCheckExists, mediaUpload,
  mediaDeleteFiles, mediaMove, mediaRename,
  getWsToken, generateWsToken,
} from '../services/api';

const TABS = [
  { id: 'bus-events', label: 'Bus Events', icon: Radio },
  { id: 'media', label: 'Media', icon: Image },
  { id: 'ws-api', label: 'WS / API', icon: Wifi },
];

function StatusBadge({ status }) {
  if (status === 'success') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 9999, padding: '2px 8px', fontSize: 11, fontWeight: 500, background: '#ecfdf5', color: '#059669' }}>
        <CheckCircle style={{ width: 12, height: 12 }} /> OK
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 9999, padding: '2px 8px', fontSize: 11, fontWeight: 500, background: '#fef2f2', color: '#dc2626' }}>
      <XCircle style={{ width: 12, height: 12 }} /> Erreur
    </span>
  );
}

function DirectionBadge({ direction }) {
  if (direction === 'published') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 9999, padding: '2px 8px', fontSize: 11, fontWeight: 500, background: '#eef2ff', color: '#4f46e5' }}>
        <ArrowUpRight style={{ width: 11, height: 11 }} /> Publié
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 9999, padding: '2px 8px', fontSize: 11, fontWeight: 500, background: '#f5f3ff', color: '#7c3aed' }}>
      <ArrowDownLeft style={{ width: 11, height: 11 }} /> Reçu
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

/* ── Payload Modal ───────────────────────────── */

function PayloadModal({ entry, onClose }) {
  if (!entry) return null;

  const payload = entry.event_data;
  let formatted = '';
  try {
    formatted = typeof payload === 'string' ? JSON.stringify(JSON.parse(payload), null, 2) : JSON.stringify(payload, null, 2);
  } catch {
    formatted = String(payload || '(vide)');
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: 16, width: '90%', maxWidth: 700, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid var(--border-color)' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Payload — {entry.routing_key}</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
              {formatDate(entry.created_at)} · <DirectionBadge direction={entry.direction} /> · <StatusBadge status={entry.processed ? 'success' : 'failed'} />
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 8, color: 'var(--text-secondary)' }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        {/* Info grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, padding: '12px 20px', borderBottom: '1px solid var(--border-color)' }}>
          {[
            { label: 'Source', value: entry.source_app || '--' },
            { label: 'Table', value: entry.ref_table || entry.event_table || '--' },
            { label: 'Action', value: entry.action || '--' },
            { label: 'Entity ID', value: entry.entity_id ?? '--' },
            { label: 'Direction', value: entry.direction === 'published' ? 'Publié' : 'Reçu' },
            { label: 'Erreur', value: entry.error || '--' },
          ].map((item) => (
            <div key={item.label}>
              <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>{item.label}</div>
              <div style={{ fontSize: 12, color: item.label === 'Erreur' && entry.error ? '#dc2626' : 'var(--text-primary)', marginTop: 2, wordBreak: 'break-all' }}>{item.value}</div>
            </div>
          ))}
        </div>

        {/* Payload JSON */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px' }}>
          <pre style={{ margin: 0, padding: 12, background: '#f8fafc', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 400, overflow: 'auto', color: 'var(--text-primary)' }}>
            {formatted}
          </pre>
        </div>
      </div>
    </div>
  );
}

/* ── Bus Events Tab ───────────────────────────── */


/**
 * Liens vers les autres applications.
 *
 * Tant que l'app Bornes n'est pas en ligne, les numéros de bornes pointent vers
 * le CRM. Le gabarit est ici plutôt qu'en dur : le jour de la bascule, il n'y a
 * qu'un champ à changer, sans redéploiement.
 */
function LiensApplications() {
  const peutEditer = usePerm('parametres.edit_integrations');
  const [valeur, setValeur] = useState('');
  const [initial, setInitial] = useState('');
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    getSetting('integrations.bornes_url')
      .then((v) => {
        const t = typeof v === 'string' ? v : v?.template || '';
        setValeur(t);
        setInitial(t);
      })
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  const enregistrer = async () => {
    setEnregistrement(true);
    try {
      await updateSetting('integrations.bornes_url', valeur.trim());
      setInitial(valeur.trim());
      toast.success('Lien enregistré — actif au prochain chargement des pages.');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Enregistrement impossible');
    } finally {
      setEnregistrement(false);
    }
  };

  const exemple = (valeur || 'https://crm.konitys.fr/fr/bornes/view/{id}')
    .replace('{id}', '1234')
    .replace('{numero}', 'C128');

  return (
    <div style={{ borderRadius: 16, background: 'var(--bg-primary)', border: '1px solid var(--border-color)', padding: '16px 20px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15 }}>Liens vers les fiches bornes</h3>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
        Adresse ouverte au clic sur un numéro de borne (carte, fiche contact, matériel).
        Utilisez <code>{'{id}'}</code> pour l'identifiant de la borne, ou <code>{'{numero}'}</code> pour
        son numéro (ex. C128). Laissez vide pour revenir à la valeur par défaut.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={valeur}
          onChange={(e) => setValeur(e.target.value)}
          disabled={!peutEditer || chargement}
          placeholder="https://crm.konitys.fr/fr/bornes/view/{id}"
          style={{ flex: '1 1 380px', minWidth: 0, padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13 }}
        />
        {peutEditer && (
          <button
            type="button"
            onClick={enregistrer}
            disabled={enregistrement || valeur.trim() === initial}
            data-perm="antennes:parametres.edit_integrations"
            style={{
              padding: '9px 16px', borderRadius: 8, border: 'none',
              background: valeur.trim() === initial ? '#c7d2fe' : '#4f46e5', color: '#fff',
              fontSize: 13, fontWeight: 600, cursor: valeur.trim() === initial ? 'default' : 'pointer',
            }}
          >
            {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        )}
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
        Aperçu : <code>{exemple}</code>
      </div>
    </div>
  );
}

function BusEventsTab() {
  const [publishedData, setPublishedData] = useState(null);
  const [subsData, setSubsData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, limit: 30, offset: 0 });
  const [logFilters, setLogFilters] = useState({ status: '', direction: '', table: '', source: '', date_from: '', date_to: '', limit: 30, offset: 0 });
  const [sources, setSources] = useState([]);
  const [loadingPublished, setLoadingPublished] = useState(true);
  const [loadingSubs, setLoadingSubs] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingLog, setLoadingLog] = useState(true);
  const [payloadEntry, setPayloadEntry] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  const loadAll = async () => {
    try {
      setLoadingPublished(true);
      setLoadingSubs(true);
      setLoadingStats(true);
      const [pubRes, subRes, statsRes, srcRes] = await Promise.all([
        api.get('/parametres/events/published'),
        api.get('/parametres/events/subscriptions'),
        api.get('/parametres/events/stats'),
        api.get('/parametres/events/sources'),
      ]);
      setPublishedData(pubRes.data.data || null);
      setSubsData(subRes.data.data || null);
      setStatsData(statsRes.data.data || null);
      setSources(srcRes.data.data || []);
    } catch (error) {
      console.error('Error loading bus events:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoadingPublished(false);
      setLoadingSubs(false);
      setLoadingStats(false);
    }
  };

  const loadLogs = async (filters) => {
    try {
      setLoadingLog(true);
      const f = filters || logFilters;
      const params = { limit: f.limit, offset: f.offset };
      if (f.status) params.status = f.status;
      if (f.direction) params.direction = f.direction;
      if (f.table) params.table = f.table;
      if (f.source) params.source = f.source;
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const res = await api.get('/parametres/events/logs', { params });
      setLogEntries(res.data.data || []);
      setPagination(res.data.pagination || { total: 0, limit: 30, offset: 0 });
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => { loadAll(); loadLogs(); }, []);
  useEffect(() => { loadLogs(); }, [logFilters]);

  const updateFilter = (key, value) => {
    setLogFilters(prev => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleClearLogs = async () => {
    try {
      await api.delete('/parametres/events/logs');
      toast.success('Logs vidés');
      setConfirmClear(false);
      loadAll();
      loadLogs();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);
    try {
      const params = {};
      if (logFilters.status) params.status = logFilters.status;
      if (logFilters.direction) params.direction = logFilters.direction;
      if (logFilters.table) params.table = logFilters.table;
      if (logFilters.source) params.source = logFilters.source;
      if (logFilters.date_from) params.date_from = logFilters.date_from;
      if (logFilters.date_to) params.date_to = logFilters.date_to;

      const res = await api.get(`/parametres/events/export/${format}`, { params, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ext = format.startsWith('sql') ? 'sql' : format;
      a.href = url;
      a.download = `event_logs.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erreur lors de l'export");
    }
  };

  const isConnected = publishedData?.connected || false;
  const totals = statsData?.totals || {};
  const subs = subsData?.subscriptions || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <LiensApplications />

      {/* RabbitMQ Connection Status */}
      <div
        style={{
          borderRadius: 16,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          background: isConnected
            ? 'linear-gradient(135deg, #ecfdf5, rgba(204,251,241,0.5))'
            : 'linear-gradient(135deg, #fef2f2, rgba(254,226,226,0.5))',
          border: isConnected ? '1px solid rgba(167,243,208,0.6)' : '1px solid rgba(254,202,202,0.6)',
          padding: '12px 20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isConnected
            ? <Wifi style={{ width: 20, height: 20, color: '#059669' }} />
            : <WifiOff style={{ width: 20, height: 20, color: '#dc2626' }} />
          }
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: isConnected ? '#047857' : '#b91c1c' }}>
              RabbitMQ — {isConnected ? 'Connecté' : 'Déconnecté'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              Exchange: {publishedData?.exchange || '-'} | App: {publishedData?.appName || '-'}
            </div>
            {publishedData?.connectionUrl && (
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                URL: {publishedData.connectionUrl}
              </div>
            )}
            {!isConnected && publishedData?.lastError && (
              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                Erreur: {publishedData.lastError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats KPI cards */}
      {!loadingStats && totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }} className="param-grid-4col">
          {[
            { label: 'Total', value: totals.total || 0, color: '#4f46e5' },
            { label: 'Publié', value: totals.published || 0, color: '#2563eb' },
            { label: 'Reçu', value: totals.received || 0, color: '#7c3aed' },
            { label: 'Erreurs', value: totals.errors || 0, color: '#dc2626' },
          ].map((kpi) => (
            <div key={kpi.label} style={{ borderRadius: 12, border: '1px solid var(--border-color)', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '10px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>{kpi.label}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color, fontVariantNumeric: 'tabular-nums' }}>
                {kpi.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Published Events + Subscriptions side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="param-grid-2col">
        {/* Published Events */}
        <div style={{ borderRadius: 16, border: '1px solid var(--border-color)', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'linear-gradient(135deg, #eef2ff, rgba(219,234,254,0.3))' }}>
            <Radio style={{ width: 15, height: 15, color: 'var(--primary-600)' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Événements publiés</span>
            {publishedData?.events && (
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
                {publishedData.events.length} tables
              </span>
            )}
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {loadingPublished ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
                <Loader2 className="spin-icon" style={{ width: 20, height: 20, color: 'var(--text-secondary)' }} />
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Table</th>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Actions</th>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Routing Key</th>
                  </tr>
                </thead>
                <tbody>
                  {publishedData?.events?.map((evt) => (
                    <tr key={evt.table} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '6px 16px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)' }}>{evt.table}</td>
                      <td style={{ padding: '6px 16px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {evt.actions.map((a) => (
                            <span key={a} style={{ borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{a}</span>
                          ))}
                        </div>
                      </td>
                      <td style={{ padding: '6px 16px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>
                        {publishedData.appName}.{evt.table}.*
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Subscriptions */}
        <div style={{ borderRadius: 16, border: '1px solid var(--border-color)', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'linear-gradient(135deg, #f5f3ff, rgba(237,233,254,0.3))' }}>
            <Download style={{ width: 15, height: 15, color: '#7c3aed' }} />
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Abonnements (RefSync)</span>
            {subs.length > 0 && (
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
                {subs.length} tables
              </span>
            )}
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {loadingSubs ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
                <Loader2 className="spin-icon" style={{ width: 20, height: 20, color: 'var(--text-secondary)' }} />
              </div>
            ) : subs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0', fontSize: 13, color: 'var(--text-secondary)' }}>Aucune table _ref trouvée</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Table _ref</th>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Pattern</th>
                    <th style={{ textAlign: 'left', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Queue</th>
                    <th style={{ textAlign: 'right', padding: '8px 16px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Cols</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((sub) => (
                    <tr key={sub.ref_table} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '6px 16px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)' }}>{sub.ref_table}</td>
                      <td style={{ padding: '6px 16px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>{sub.amqp_pattern}</td>
                      <td style={{ padding: '6px 16px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>{sub.queue_name}</td>
                      <td style={{ padding: '6px 16px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)' }}>{sub.columns_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Events Log */}
      <div style={{ borderRadius: 16, border: '1px solid var(--border-color)', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: 'linear-gradient(135deg, #fefce8, rgba(254,249,195,0.3))' }}>
          <History style={{ width: 15, height: 15, color: '#ca8a04' }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Historique des événements</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {pagination.total} total
          </span>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '8px 16px', borderBottom: '1px solid var(--border-color)' }}>
          <select
            value={logFilters.direction}
            onChange={(e) => updateFilter('direction', e.target.value)}
            style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)' }}
          >
            <option value="">Toutes directions</option>
            <option value="published">Publié</option>
            <option value="received">Reçu</option>
          </select>
          <select
            value={logFilters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
            style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)' }}
          >
            <option value="">Tous statuts</option>
            <option value="success">Succès</option>
            <option value="failed">Erreurs</option>
          </select>
          <input
            type="text"
            placeholder="Filtrer par table..."
            value={logFilters.table}
            onChange={(e) => updateFilter('table', e.target.value)}
            style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)', width: 160 }}
          />
          <select
            value={logFilters.source}
            onChange={(e) => updateFilter('source', e.target.value)}
            style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-primary)' }}
          >
            <option value="">Toutes sources</option>
            {sources.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input
              type="date"
              value={logFilters.date_from}
              onChange={(e) => updateFilter('date_from', e.target.value)}
              title="Date début"
              style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: logFilters.date_from ? 'var(--text-primary)' : 'var(--text-secondary)', width: 130 }}
            />
            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>→</span>
            <input
              type="date"
              value={logFilters.date_to}
              onChange={(e) => updateFilter('date_to', e.target.value)}
              title="Date fin"
              style={{ borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: logFilters.date_to ? 'var(--text-primary)' : 'var(--text-secondary)', width: 130 }}
            />
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Export dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowExportMenu(prev => !prev)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-secondary)', background: 'transparent', cursor: 'pointer' }}
              >
                <FileDown style={{ width: 12, height: 12 }} /> Exporter
              </button>
              {showExportMenu && (
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowExportMenu(false)} />
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: 'white', border: '1px solid var(--border-color)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 180, overflow: 'hidden' }}>
                    {[
                      { format: 'xlsx', label: 'Excel (.xlsx)' },
                      { format: 'csv', label: 'CSV (.csv)' },
                      { format: 'pdf', label: 'PDF (.pdf)' },
                      { format: 'json', label: 'JSON (.json)' },
                      { format: 'sql-full', label: 'SQL (table + data)' },
                      { format: 'sql-data', label: 'SQL (data only)' },
                    ].map((item) => (
                      <button
                        key={item.format}
                        onClick={() => handleExport(item.format)}
                        style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 12, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-primary)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Clear logs */}
            {!confirmClear ? (
              <button
                onClick={() => setConfirmClear(true)}
                title="Vider les logs"
                style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-secondary)', background: 'transparent', cursor: 'pointer' }}
              >
                <Trash2 style={{ width: 12, height: 12 }} /> Vider
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 500 }}>Confirmer ?</span>
                <button
                  onClick={handleClearLogs}
                  style={{ borderRadius: 6, border: 'none', padding: '3px 10px', fontSize: 11, fontWeight: 600, background: '#dc2626', color: 'white', cursor: 'pointer' }}
                >
                  Oui
                </button>
                <button
                  onClick={() => setConfirmClear(false)}
                  style={{ borderRadius: 6, border: '1px solid var(--border-color)', padding: '3px 10px', fontSize: 11, background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
                >
                  Non
                </button>
              </div>
            )}

            {/* Refresh */}
            <button
              onClick={() => { loadAll(); loadLogs(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid var(--border-color)', padding: '4px 8px', fontSize: 12, color: 'var(--text-secondary)', background: 'transparent', cursor: 'pointer' }}
            >
              <RefreshCw style={{ width: 12, height: 12 }} /> Rafraîchir
            </button>
          </div>
        </div>

        {/* Log table */}
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          {loadingLog ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px 0' }}>
              <Loader2 className="spin-icon" style={{ width: 20, height: 20, color: 'var(--text-secondary)' }} />
            </div>
          ) : logEntries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', fontSize: 13, color: 'var(--text-secondary)' }}>Aucun événement enregistré</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Direction</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Source</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Table</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Action</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Status</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}>Erreur</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 10 }}></th>
                </tr>
              </thead>
              <tbody>
                {logEntries.map((entry) => (
                  <tr key={entry.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{formatDate(entry.created_at)}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <DirectionBadge direction={entry.direction} />
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      <span style={{ display: 'inline-block', padding: '2px 8px', background: '#f1f5f9', borderRadius: 9999, fontSize: 11, fontWeight: 500, color: '#475569' }}>{entry.source_app || '--'}</span>
                    </td>
                    <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: 11, color: 'var(--text-primary)' }}>{entry.ref_table || entry.event_table}</td>
                    <td style={{ padding: '6px 12px' }}>
                      <span style={{ borderRadius: 4, padding: '2px 6px', fontSize: 10, fontWeight: 500, background: '#f1f5f9', color: '#475569' }}>{entry.action}</span>
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      <StatusBadge status={entry.processed ? 'success' : 'failed'} />
                    </td>
                    <td style={{ padding: '6px 12px', fontSize: 11, color: '#dc2626', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.error || ''}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <button
                        onClick={() => setPayloadEntry(entry)}
                        title="Voir le payload"
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 6, color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = 'var(--primary-600)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      >
                        <Eye style={{ width: 14, height: 14 }} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (() => {
          const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
          const totalPages = Math.ceil(pagination.total / pagination.limit);
          const goToPage = (page) => setLogFilters(prev => ({ ...prev, offset: (page - 1) * prev.limit }));

          // Build page numbers to show
          const pages = [];
          if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
          } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
          }

          const btnStyle = (active) => ({
            borderRadius: 6, border: active ? '1px solid var(--primary-600)' : '1px solid var(--border-color)',
            padding: '3px 8px', fontSize: 11, fontWeight: active ? 600 : 400,
            background: active ? 'var(--primary-600)' : 'transparent', color: active ? 'white' : 'var(--text-secondary)',
            cursor: 'pointer', minWidth: 28, textAlign: 'center',
          });
          const navBtn = (disabled) => ({
            borderRadius: 6, border: '1px solid var(--border-color)', padding: 4,
            background: 'transparent', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.3 : 1,
          });

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 16px', borderTop: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} sur {pagination.total}
                </span>
                <select
                  value={logFilters.limit}
                  onChange={(e) => setLogFilters(prev => ({ ...prev, limit: parseInt(e.target.value), offset: 0 }))}
                  style={{ borderRadius: 6, border: '1px solid var(--border-color)', padding: '2px 4px', fontSize: 11, color: 'var(--text-secondary)' }}
                >
                  {[10, 20, 30, 50, 100].map(n => (
                    <option key={n} value={n}>{n} / page</option>
                  ))}
                </select>
              </div>
              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <button disabled={currentPage === 1} onClick={() => goToPage(1)} style={navBtn(currentPage === 1)}>
                    <ChevronsLeft style={{ width: 13, height: 13, color: 'var(--text-secondary)' }} />
                  </button>
                  <button disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)} style={navBtn(currentPage === 1)}>
                    <ChevronLeft style={{ width: 13, height: 13, color: 'var(--text-secondary)' }} />
                  </button>
                  {pages.map((p, i) =>
                    p === '...' ? (
                      <span key={`dot-${i}`} style={{ fontSize: 11, color: 'var(--text-secondary)', padding: '0 2px' }}>…</span>
                    ) : (
                      <button key={p} onClick={() => goToPage(p)} style={btnStyle(p === currentPage)}>{p}</button>
                    )
                  )}
                  <button disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)} style={navBtn(currentPage === totalPages)}>
                    <ChevronRight style={{ width: 13, height: 13, color: 'var(--text-secondary)' }} />
                  </button>
                  <button disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)} style={navBtn(currentPage === totalPages)}>
                    <ChevronsRight style={{ width: 13, height: 13, color: 'var(--text-secondary)' }} />
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Payload Modal */}
      {payloadEntry && <PayloadModal entry={payloadEntry} onClose={() => setPayloadEntry(null)} />}
    </div>
  );
}

/* ── Media Tab ────────────────────────────────── */

const FILE_ICONS = {
  folder: Folder,
  image: Image,
  default: File,
};

function getFileIcon(item) {
  if (item.isDirectory) return FILE_ICONS.folder;
  const ext = item.name.split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'tiff', 'bmp', 'svg'].includes(ext)) return FILE_ICONS.image;
  return FILE_ICONS.default;
}

function formatFileSize(bytes) {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const MEDIA_PAGE_SIZE = 50;

function MediaTab() {
  const [currentPath, setCurrentPath] = useState('/');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [mediaPage, setMediaPage] = useState(1);

  // Modals
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showRename, setShowRename] = useState(null); // item to rename
  const [renameValue, setRenameValue] = useState('');
  const [showMove, setShowMove] = useState(false);
  const [moveDestination, setMoveDestination] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showClearAllConfirm, setShowClearAllConfirm] = useState(false);
  const [showUploadConflict, setShowUploadConflict] = useState(null); // { files, existing }
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [contextMenu, setContextMenu] = useState(null); // { item, x, y }

  const fileInputRef = useState(null);

  const loadItems = async (path) => {
    try {
      setLoading(true);
      const res = await mediaList(path || '/');
      setItems(res.data?.items || []);
      setSelected(new Set());
      setMediaPage(1);
    } catch (err) {
      toast.error('Erreur chargement fichiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadItems(currentPath); }, [currentPath]);

  const breadcrumbs = () => {
    const parts = currentPath.split('/').filter(Boolean);
    const crumbs = [{ label: 'uploads', path: '/' }];
    let acc = '';
    for (const p of parts) {
      acc += `/${p}`;
      crumbs.push({ label: p, path: acc });
    }
    return crumbs;
  };

  const navigateTo = (path) => {
    setCurrentPath(path);
  };

  const handleItemClick = (item) => {
    if (item.isDirectory) {
      navigateTo(item.path);
    }
  };

  const toggleSelect = (item, e) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      const key = item.path;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.path)));
    }
  };

  // Create folder
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await mediaCreateFolder(currentPath === '/' ? '' : currentPath, newFolderName.trim());
      toast.success(`Dossier "${newFolderName}" cree`);
      setShowNewFolder(false);
      setNewFolderName('');
      loadItems(currentPath);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur creation dossier');
    }
  };

  // Upload files
  const handleUploadClick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (!files.length) return;

      // Check existing
      try {
        const res = await mediaCheckExists(
          currentPath === '/' ? '' : currentPath,
          files.map(f => f.name)
        );
        const existing = res.data?.existing || [];
        if (existing.length > 0) {
          setShowUploadConflict({ files, existing });
          return;
        }
      } catch { /* proceed */ }

      doUpload(files, 'skip');
    };
    input.click();
  };

  const doUpload = async (files, conflict) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      // For skip: filter out conflicting files on the client — no need to send them
      const filesToSend = conflict === 'skip' && showUploadConflict?.existing?.length
        ? files.filter(f => !showUploadConflict.existing.includes(f.name))
        : files;
      if (!filesToSend.length) {
        toast.info('Tous les fichiers existent déjà — aucun envoi');
        setShowUploadConflict(null);
        return;
      }
      await mediaUpload(
        currentPath === '/' ? '' : currentPath,
        filesToSend,
        conflict,
        (e) => { if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100)); }
      );
      toast.success(`${files.length} fichier(s) uploade(s)`);
      setShowUploadConflict(null);
      loadItems(currentPath);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete
  const handleDelete = async () => {
    try {
      setDeleting(true);
      const files = Array.from(selected);
      const res = await mediaDeleteFiles(files);
      const deleted = res?.data?.deleted || files.length;
      const errors = res?.data?.errors || [];
      if (errors.length > 0) {
        toast.warn(`${deleted} supprime(s), ${errors.length} erreur(s)`);
        console.warn('[Media] Delete errors:', errors);
      } else {
        toast.success(`${deleted} element(s) supprime(s)`);
      }
      setShowDeleteConfirm(false);
      setSelected(new Set());
      loadItems(currentPath);
    } catch (err) {
      console.error('[Media] Delete failed:', err);
      toast.error(err.response?.data?.error || err.message || 'Erreur suppression');
    } finally {
      setDeleting(false);
    }
  };

  // Clear all uploads
  const handleClearAll = async () => {
    try {
      await api.delete('/media/clear-all');
      toast.success('Dossier uploads vide');
      setShowClearAllConfirm(false);
      setSelected(new Set());
      loadItems('/');
      setCurrentPath('/');
    } catch (err) {
      console.error('[Media] Clear all failed:', err);
      toast.error(err.response?.data?.error || 'Erreur vidage uploads');
    }
  };

  // Rename
  const handleRename = async () => {
    if (!renameValue.trim() || !showRename) return;
    try {
      await mediaRename(showRename.path, renameValue.trim());
      toast.success('Renomme avec succes');
      setShowRename(null);
      setRenameValue('');
      loadItems(currentPath);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur renommage');
    }
  };

  // Move
  const handleMove = async () => {
    try {
      const files = Array.from(selected);
      await mediaMove(files, moveDestination);
      toast.success(`${files.length} element(s) deplace(s)`);
      setShowMove(false);
      setMoveDestination('');
      loadItems(currentPath);
    } catch (err) {
      toast.error('Erreur deplacement');
    }
  };

  // Context menu
  const handleContextMenu = (item, e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ item, x: e.clientX, y: e.clientY });
  };

  const closeContextMenu = () => setContextMenu(null);

  useEffect(() => {
    const handler = () => closeContextMenu();
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, []);

  const cardStyle = {
    borderRadius: 16, border: '1px solid var(--border-color)', background: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
  };

  const totalMediaPages = Math.ceil(items.length / MEDIA_PAGE_SIZE);
  const pagedItems = items.slice((mediaPage - 1) * MEDIA_PAGE_SIZE, mediaPage * MEDIA_PAGE_SIZE);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Toolbar */}
      <div style={{ ...cardStyle, padding: '10px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 200 }}>
            {currentPath !== '/' && (
              <button
                onClick={() => {
                  const parent = currentPath.split('/').slice(0, -1).join('/') || '/';
                  navigateTo(parent);
                }}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'inline-flex', color: 'var(--text-secondary)' }}
              >
                <ArrowLeft style={{ width: 16, height: 16 }} />
              </button>
            )}
            {breadcrumbs().map((crumb, i, arr) => (
              <span key={crumb.path} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {i > 0 && <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>/</span>}
                <button
                  onClick={() => navigateTo(crumb.path)}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer', padding: '2px 4px',
                    borderRadius: 4, fontSize: 13, color: i === arr.length - 1 ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontWeight: i === arr.length - 1 ? 600 : 400,
                  }}
                >
                  {i === 0 ? <FolderOpen style={{ width: 14, height: 14, verticalAlign: 'middle' }} /> : crumb.label}
                </button>
              </span>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              onClick={() => setShowNewFolder(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid var(--border-color)', padding: '5px 10px', fontSize: 12, color: 'var(--text-secondary)', background: 'transparent', cursor: 'pointer' }}
            >
              <FolderPlus style={{ width: 14, height: 14 }} /> Nouveau dossier
            </button>
            <button
              onClick={handleUploadClick}
              disabled={uploading}
              style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: 'none', padding: '5px 12px', fontSize: 12, fontWeight: 600, color: 'white', background: 'var(--primary-600)', cursor: 'pointer', opacity: uploading ? 0.6 : 1 }}
            >
              <Upload style={{ width: 14, height: 14 }} /> {uploading ? `Upload ${uploadProgress}%` : 'Uploader'}
            </button>
            {selected.size > 0 && (
              <>
                <button
                  onClick={() => setShowMove(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid var(--border-color)', padding: '5px 10px', fontSize: 12, color: 'var(--text-secondary)', background: 'transparent', cursor: 'pointer' }}
                >
                  <Move style={{ width: 14, height: 14 }} /> Deplacer ({selected.size})
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid #fca5a5', padding: '5px 10px', fontSize: 12, color: '#dc2626', background: '#fef2f2', cursor: 'pointer' }}
                >
                  <Trash2 style={{ width: 14, height: 14 }} /> Supprimer ({selected.size})
                </button>
              </>
            )}
            <button
              onClick={() => loadItems(currentPath)}
              style={{ display: 'flex', alignItems: 'center', borderRadius: 8, border: '1px solid var(--border-color)', padding: 5, background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <RefreshCw style={{ width: 14, height: 14 }} />
            </button>
            <button
              onClick={() => setShowClearAllConfirm(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8, border: '1px solid #fca5a5', padding: '5px 10px', fontSize: 12, color: '#dc2626', background: '#fef2f2', cursor: 'pointer', marginLeft: 'auto' }}
            >
              <Trash2 style={{ width: 14, height: 14 }} /> Vider tout
            </button>
          </div>
        </div>
      </div>

      {/* File list */}
      <div style={cardStyle}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
            <Loader2 className="spin-icon" style={{ width: 24, height: 24, color: 'var(--text-secondary)' }} />
          </div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
            <Folder style={{ width: 40, height: 40, marginBottom: 8, opacity: 0.3 }} />
            <div>Dossier vide</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <th style={{ width: 32, padding: '8px 8px 8px 12px' }}>
                  <input
                    type="checkbox"
                    checked={pagedItems.length > 0 && pagedItems.every(i => selected.has(i.path))}
                    onChange={selectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Nom</th>
                <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, width: 100 }}>Taille</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, width: 150 }}>Modifie</th>
                <th style={{ width: 40, padding: '8px 12px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map((item) => {
                const Icon = getFileIcon(item);
                const isSelected = selected.has(item.path);
                return (
                  <tr
                    key={item.path}
                    style={{
                      borderBottom: '1px solid var(--border-color)',
                      background: isSelected ? '#eef2ff' : 'transparent',
                      cursor: item.isDirectory ? 'pointer' : 'default',
                    }}
                    onClick={() => handleItemClick(item)}
                    onContextMenu={(e) => handleContextMenu(item, e)}
                  >
                    <td style={{ padding: '6px 8px 6px 12px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => toggleSelect(item, e)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '6px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Icon style={{ width: 16, height: 16, color: item.isDirectory ? '#f59e0b' : 'var(--text-secondary)', flexShrink: 0 }} />
                        <span style={{ fontWeight: item.isDirectory ? 500 : 400, color: 'var(--text-primary)' }}>
                          {item.name}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '6px 12px', textAlign: 'right', color: 'var(--text-secondary)', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                      {formatFileSize(item.size)}
                    </td>
                    <td style={{ padding: '6px 12px', color: 'var(--text-secondary)', fontSize: 12 }}>
                      {new Date(item.modified).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleContextMenu(item, e); }}
                        style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 6, color: 'var(--text-secondary)', display: 'inline-flex' }}
                      >
                        <MoreVertical style={{ width: 14, height: 14 }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {/* Pagination */}
        {!loading && items.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--border-color)', fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {totalMediaPages > 1
                ? `${(mediaPage - 1) * MEDIA_PAGE_SIZE + 1}–${Math.min(mediaPage * MEDIA_PAGE_SIZE, items.length)} / ${items.length} fichier${items.length > 1 ? 's' : ''}`
                : `${items.length} fichier${items.length > 1 ? 's' : ''}`}
            </span>
            {totalMediaPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button onClick={() => setMediaPage(1)} disabled={mediaPage === 1} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: mediaPage === 1 ? 'not-allowed' : 'pointer', opacity: mediaPage === 1 ? 0.4 : 1, display: 'inline-flex' }}><ChevronsLeft style={{ width: 14, height: 14 }} /></button>
                <button onClick={() => setMediaPage(p => p - 1)} disabled={mediaPage === 1} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: mediaPage === 1 ? 'not-allowed' : 'pointer', opacity: mediaPage === 1 ? 0.4 : 1, display: 'inline-flex' }}><ChevronLeft style={{ width: 14, height: 14 }} /></button>
                <span style={{ padding: '3px 10px', color: 'var(--text-primary)' }}>{mediaPage} / {totalMediaPages}</span>
                <button onClick={() => setMediaPage(p => p + 1)} disabled={mediaPage === totalMediaPages} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: mediaPage === totalMediaPages ? 'not-allowed' : 'pointer', opacity: mediaPage === totalMediaPages ? 0.4 : 1, display: 'inline-flex' }}><ChevronRight style={{ width: 14, height: 14 }} /></button>
                <button onClick={() => setMediaPage(totalMediaPages)} disabled={mediaPage === totalMediaPages} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: mediaPage === totalMediaPages ? 'not-allowed' : 'pointer', opacity: mediaPage === totalMediaPages ? 0.4 : 1, display: 'inline-flex' }}><ChevronsRight style={{ width: 14, height: 14 }} /></button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 998 }} onClick={closeContextMenu} />
          <div
            style={{
              position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 999,
              background: 'white', border: '1px solid var(--border-color)', borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 160, overflow: 'hidden', padding: '4px 0',
            }}
          >
            {contextMenu.item.isDirectory && (
              <button
                onClick={() => { navigateTo(contextMenu.item.path); closeContextMenu(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', textAlign: 'left' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <FolderOpen style={{ width: 14, height: 14, color: 'var(--text-secondary)' }} /> Ouvrir
              </button>
            )}
            <button
              onClick={() => {
                setShowRename(contextMenu.item);
                setRenameValue(contextMenu.item.name);
                closeContextMenu();
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', textAlign: 'left' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Edit3 style={{ width: 14, height: 14, color: 'var(--text-secondary)' }} /> Renommer
            </button>
            <button
              onClick={() => {
                setSelected(new Set([contextMenu.item.path]));
                setShowMove(true);
                closeContextMenu();
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)', textAlign: 'left' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Move style={{ width: 14, height: 14, color: 'var(--text-secondary)' }} /> Deplacer
            </button>
            <div style={{ height: 1, background: 'var(--border-color)', margin: '4px 0' }} />
            <button
              onClick={() => {
                setSelected(new Set([contextMenu.item.path]));
                setShowDeleteConfirm(true);
                closeContextMenu();
              }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: '#dc2626', textAlign: 'left' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <Trash2 style={{ width: 14, height: 14 }} /> Supprimer
            </button>
          </div>
        </>
      )}

      {/* New Folder Modal */}
      {showNewFolder && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowNewFolder(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 380, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Nouveau dossier</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
              placeholder="Nom du dossier"
              autoFocus
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowNewFolder(false)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Annuler</button>
              <button onClick={handleCreateFolder} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: 'var(--primary-600)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Creer</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {showRename && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowRename(null)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 380, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Renommer</h3>
            <input
              type="text"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleRename()}
              autoFocus
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowRename(null)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Annuler</button>
              <button onClick={handleRename} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: 'var(--primary-600)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Renommer</button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowMove(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 400, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Deplacer {selected.size} element(s)</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 16px' }}>Chemin de destination (relatif a uploads)</p>
            <input
              type="text"
              value={moveDestination}
              onChange={e => setMoveDestination(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleMove()}
              placeholder="ex: photos/2024"
              autoFocus
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-color)', fontSize: 13, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowMove(false)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Annuler</button>
              <button onClick={handleMove} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: 'var(--primary-600)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Deplacer</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={!deleting ? () => setShowDeleteConfirm(false) : undefined}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 380, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#dc2626' }}>Confirmer la suppression</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
              Supprimer {selected.size} element(s){items.some(it => selected.has(it.path) && it.isDirectory) ? ', y compris le contenu des dossiers' : ''} ? Cette action est irreversible.
            </p>
            {deleting ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <Loader2 className="spin-icon" style={{ width: 16, height: 16, color: '#dc2626', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                    {items.some(it => selected.has(it.path) && it.isDirectory) ? 'Suppression du contenu du dossier...' : 'Suppression en cours...'}
                  </span>
                </div>
                <div style={{ height: 4, background: '#fee2e2', borderRadius: 4, overflow: 'hidden' }}>
                  <div className="progress-bar-indeterminate" style={{ height: '100%', width: '35%', background: '#dc2626', borderRadius: 4 }} />
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setShowDeleteConfirm(false)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Annuler</button>
                <button onClick={handleDelete} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Supprimer</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Clear All Confirm Modal */}
      {showClearAllConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowClearAllConfirm(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 380, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#dc2626' }}>Vider le dossier uploads</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
              Supprimer TOUS les fichiers et dossiers dans /uploads ? Cette action est irreversible.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowClearAllConfirm(false)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Annuler</button>
              <button onClick={handleClearAll} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Vider tout</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Conflict Modal */}
      {showUploadConflict && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowUploadConflict(null)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 440, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#f59e0b' }}>Fichier(s) existant(s)</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
              {showUploadConflict.existing.length} fichier(s) existe(nt) deja :
            </p>
            <div style={{ maxHeight: 120, overflowY: 'auto', marginBottom: 16, padding: 8, background: '#f8fafc', borderRadius: 8, border: '1px solid var(--border-color)' }}>
              {showUploadConflict.existing.map(name => (
                <div key={name} style={{ fontSize: 12, padding: '2px 0', fontFamily: 'monospace', color: 'var(--text-primary)' }}>{name}</div>
              ))}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 12px' }}>Que souhaitez-vous faire ?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => doUpload(showUploadConflict.files, 'skip')}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'white', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', textAlign: 'left' }}
              >
                <strong>Ignorer</strong> — conserver les fichiers existants
              </button>
              <button
                onClick={() => doUpload(showUploadConflict.files, 'overwrite')}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #dc2626', background: '#fff5f5', cursor: 'pointer', fontSize: 13, color: '#dc2626', textAlign: 'left' }}
              >
                <strong>Remplacer</strong> — écraser les anciens par les nouveaux
              </button>
              <button
                onClick={() => doUpload(showUploadConflict.files, 'rename-old')}
                style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'white', cursor: 'pointer', fontSize: 13, color: 'var(--text-primary)', textAlign: 'left' }}
              >
                <strong>Garder les deux</strong> — renommer l'ancien (doc_1.pdf, doc_2.pdf…)
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
              <button onClick={() => setShowUploadConflict(null)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── WS / API tab ──────────────────────────────── */

const WS_BASE = API_BASE_URL.replace(/\/api(\/.*)?$/, '');

const API_ENDPOINTS = [
  {
    method: 'GET',
    path: '/ws/antennes/all',
    name: 'getAll',
    params: [],
    description: 'Toutes les antennes avec coordonnées GPS, triées par ville_principale',
    origin: 'getAll()',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/:id',
    name: 'getAntenne',
    params: ['id'],
    description: 'Une antenne par son ID',
    origin: 'getAntenne($id)',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/ville/:ville',
    name: 'getAntenneByVille',
    params: ['ville'],
    description: 'Antenne par ville exacte (ville_exacte)',
    origin: 'getAntenneByVille($ville)',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/pays/:pays',
    name: 'getAntenneByPays',
    params: ['pays'],
    description: 'Antennes par nom de pays (adresse LIKE), groupées par 6',
    origin: 'getAntenneByPays($pays)',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/pays-id/:paysId',
    name: 'getAntenneByPays2',
    params: ['paysId'],
    description: 'Antennes par pays_id, groupées par 6',
    origin: 'getAntenneByPays2($pays_id)',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/luxembourg',
    name: 'getAntenneLuxembourg',
    params: [],
    description: 'Antennes au Luxembourg, groupées par 5',
    origin: 'getAntenneLuxembourg()',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/secteur-geo',
    name: 'getAntenneBySecteurGeo',
    params: [],
    description: 'Antennes groupées par secteur géographique',
    origin: 'getAntenneBySecteurGeo()',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/antennes/country',
    name: 'getAntenneByCountry',
    params: [],
    description: 'Antennes groupées par nom de pays',
    origin: 'getAntenneByCountry()',
    status: 'done',
  },
  {
    method: 'GET',
    path: '/ws/contacts',
    name: 'getContacts',
    params: [],
    description: 'Liste des contacts avec filtres avancés',
    origin: 'Nouveau — API contacts',
    status: 'done',
    doc: {
      title: 'API Contacts — Filtres et colonnes personnalisables',
      sections: [
        {
          title: 'Filtres disponibles',
          items: [
            { param: 'email_only', type: '0 | 1', desc: 'Uniquement ceux avec email renseigné (1) ou sans email (0)', example: 'email_only=1' },
            { param: 'portable_only', type: '0 | 1', desc: 'Uniquement ceux avec portable renseigné (1) ou sans (0)', example: 'portable_only=1' },
            { param: 'qualification', type: 'string (csv)', desc: 'Filtrer par type(s) de qualification (séparés par virgule)', example: 'qualification=antenne_principale,antenne_binome', values: ['antenne_principale', 'historique_antenne', 'profil_intéressant', 'antenne_binome', '(vide)'] },
            { param: 'borne_types', type: 'string (csv)', desc: 'Filtrer par type(s) de bornes assignées au contact', example: 'borne_types=classik,spherik', values: ['classik (C*)', 'spherik (S*)', 'prestige (P*)', 'accessoire (A*)'] },
            { param: 'anciennete', type: 'string', desc: 'Filtrer par ancienneté (basé sur travaille_depuis)', example: 'anciennete=>5', values: ['<1 (moins d\'1 an)', '1-3 (1 à 3 ans)', '3-5 (3 à 5 ans)', '>5 (plus de 5 ans)'] },
            { param: 'ne_plus_contacter', type: '0 | 1', desc: 'Filtrer les contacts marqués "ne plus contacter"', example: 'ne_plus_contacter=0' },
            { param: 'etat', type: 'string', desc: 'Filtrer par état du contact', example: 'etat=actif', values: ['actif', 'inactif'] },
            { param: 'antenne_id', type: 'integer', desc: 'Filtrer par ID d\'antenne', example: 'antenne_id=13' },
            { param: 'search', type: 'string', desc: 'Recherche dans nom, prénom, email, ville, raison_sociale', example: 'search=dupont' },
          ],
        },
        {
          title: 'Colonnes personnalisables',
          items: [
            { param: 'columns', type: 'string (csv)', desc: 'Liste des colonnes à retourner. Si non spécifié, retourne les colonnes par défaut.', example: 'columns=id,nom,prenom,email,tel_portable,antenne_ville' },
          ],
          extra: 'Colonnes par défaut : id, nom, prenom, civilite, email, tel_portable, tel_fixe, qualification, ne_plus_contacter, raison_sociale, antenne_id, antenne_principale, etat, ville, cp, travaille_depuis, borne_ok\n\nToutes les colonnes disponibles : id, nom, prenom, civilite, email, tel_portable, tel_fixe, date_naiss, adresse, cp, ville, pays_id, qualification, ne_plus_contacter, raison_sociale, siret, antenne_id, antenne_principale, etat, travaille_depuis, borne_ok, photo, linkedin, site_internet, distance, created, modified, addr_lat, addr_lng, situation_professionnelle, bon_savoir, antenne_ville, pays_nom',
        },
        {
          title: 'Pagination et tri',
          items: [
            { param: 'page', type: 'integer', desc: 'Numéro de page (défaut: 1)', example: 'page=2' },
            { param: 'limit', type: 'integer', desc: 'Nombre de résultats par page (défaut: 50, max: 500)', example: 'limit=100' },
            { param: 'sort', type: 'string', desc: 'Colonne de tri (défaut: nom)', example: 'sort=created' },
            { param: 'order', type: 'ASC | DESC', desc: 'Ordre de tri (défaut: ASC)', example: 'order=DESC' },
          ],
        },
        {
          title: 'Exemples complets',
          examples: [
            { label: 'Contacts avec email et portable, qualification principale', url: '/ws/contacts?email_only=1&portable_only=1&qualification=antenne_principale' },
            { label: 'Contacts avec bornes Classik ou Sphérik, ancienneté > 5 ans', url: '/ws/contacts?borne_types=classik,spherik&anciennete=>5' },
            { label: 'Contacts actifs, colonnes personnalisées, triés par date', url: '/ws/contacts?etat=actif&columns=id,nom,prenom,email,antenne_ville&sort=created&order=DESC' },
            { label: 'Recherche "dupont", page 2, 20 résultats', url: '/ws/contacts?search=dupont&limit=20&page=2' },
          ],
        },
      ],
    },
  },
];

const ENDPOINTS_PAGE_SIZE = 5;

function WsApiTab() {
  const cardStyle = {
    borderRadius: 16, border: '1px solid var(--border-color)', background: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
  };

  const methodColor = {
    GET: { bg: '#ecfdf5', color: '#059669' },
    POST: { bg: '#eef2ff', color: '#4f46e5' },
    PUT: { bg: '#fefce8', color: '#ca8a04' },
    DELETE: { bg: '#fef2f2', color: '#dc2626' },
  };

  // Token state
  const [wsToken, setWsToken] = useState(null);
  const [wsTokenUpdatedAt, setWsTokenUpdatedAt] = useState(null);
  const [wsTokenLoading, setWsTokenLoading] = useState(true);
  const [wsTokenVisible, setWsTokenVisible] = useState(false);
  const [wsTokenGenerating, setWsTokenGenerating] = useState(false);
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false);

  // Endpoint params (map: endpointName → { paramName: value })
  const [endpointParams, setEndpointParams] = useState({});

  // Preview panel
  const [preview, setPreview] = useState(null); // { name, loading, data, error }

  // Doc modal
  const [docEndpoint, setDocEndpoint] = useState(null);

  // Endpoints pagination
  const [endpointsPage, setEndpointsPage] = useState(1);
  const totalEndpointsPages = Math.ceil(API_ENDPOINTS.length / ENDPOINTS_PAGE_SIZE);
  const pagedEndpoints = API_ENDPOINTS.slice(
    (endpointsPage - 1) * ENDPOINTS_PAGE_SIZE,
    endpointsPage * ENDPOINTS_PAGE_SIZE
  );

  useEffect(() => {
    getWsToken()
      .then(d => { setWsToken(d.token); setWsTokenUpdatedAt(d.updated_at); })
      .catch(() => {})
      .finally(() => setWsTokenLoading(false));
  }, []);

  const handleGenerateToken = async () => {
    try {
      setWsTokenGenerating(true);
      const d = await generateWsToken();
      setWsToken(d.token);
      setWsTokenUpdatedAt(d.updated_at);
      setShowGenerateConfirm(false);
      toast.success('Nouveau token WS généré');
    } catch {
      toast.error('Erreur génération token');
    } finally {
      setWsTokenGenerating(false);
    }
  };

  const maskToken = (t) => t ? `${t.slice(0, 8)}••••••••••••••••${t.slice(-4)}` : '';

  const setParam = (epName, paramName, value) =>
    setEndpointParams(prev => ({
      ...prev,
      [epName]: { ...(prev[epName] || {}), [paramName]: value },
    }));

  const buildPath = (ep) => {
    let path = ep.path;
    const params = endpointParams[ep.name] || {};
    ep.params.forEach(p => {
      path = path.replace(`:${p}`, encodeURIComponent(params[p] || `{${p}}`));
    });
    return path;
  };

  const buildFullUrl = (ep, withToken = true) => {
    const path = buildPath(ep);
    const tokenSuffix = withToken && wsToken ? `?ws_token=${wsToken}` : '';
    return `${WS_BASE}${path}${tokenSuffix}`;
  };

  const handleCopyUrl = async (ep) => {
    try {
      await navigator.clipboard.writeText(buildFullUrl(ep));
      toast.success('URL copiée');
    } catch {
      toast.error('Erreur copie');
    }
  };

  const handleTestNewTab = (ep) => {
    window.open(buildFullUrl(ep), '_blank', 'noopener,noreferrer');
  };

  const handlePreview = async (ep) => {
    setPreview({ name: ep.name, loading: true, data: null, error: null });
    try {
      const resp = await fetch(`${WS_BASE}${buildPath(ep)}`, {
        headers: wsToken ? { Authorization: `Bearer ${wsToken}` } : {},
      });
      const data = await resp.json();
      if (!resp.ok) {
        setPreview({ name: ep.name, loading: false, data: null, error: data.error || `Erreur ${resp.status}` });
      } else {
        setPreview({ name: ep.name, loading: false, data, error: null });
      }
    } catch (err) {
      setPreview({ name: ep.name, loading: false, data: null, error: err.message });
    }
  };

  const btnStyle = (color) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${color || 'var(--border-color)'}`, borderRadius: 6,
    padding: '3px 6px', background: 'transparent', cursor: 'pointer',
    color: color || 'var(--text-secondary)',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Token card ── */}
      <div style={{ ...cardStyle, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Key style={{ width: 16, height: 16, color: 'var(--primary-600)' }} />
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Token WS API</h2>
          </div>
          <button
            onClick={() => setShowGenerateConfirm(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, borderRadius: 8, border: 'none', padding: '6px 14px', fontSize: 12, fontWeight: 600, color: 'white', background: 'var(--primary-600)', cursor: 'pointer' }}
          >
            <RefreshCw style={{ width: 13, height: 13 }} /> Générer nouveau token
          </button>
        </div>

        {wsTokenLoading ? (
          <Loader2 className="spin-icon" style={{ width: 18, height: 18, color: 'var(--text-secondary)' }} />
        ) : wsToken ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 12px' }}>
              <code style={{ flex: 1, fontSize: 13, fontFamily: 'monospace', color: 'var(--text-primary)', wordBreak: 'break-all' }}>
                {wsTokenVisible ? wsToken : maskToken(wsToken)}
              </code>
              <button onClick={() => setWsTokenVisible(v => !v)} style={btnStyle()} title={wsTokenVisible ? 'Masquer' : 'Afficher'}>
                {wsTokenVisible ? <EyeOff style={{ width: 14, height: 14 }} /> : <Eye style={{ width: 14, height: 14 }} />}
              </button>
              <button onClick={async () => { await navigator.clipboard.writeText(wsToken); toast.success('Token copié'); }} style={btnStyle()} title="Copier le token">
                <Copy style={{ width: 14, height: 14 }} />
              </button>
            </div>
            {wsTokenUpdatedAt && (
              <p style={{ margin: 0, fontSize: 11, color: 'var(--text-secondary)' }}>
                Généré le {new Date(wsTokenUpdatedAt).toLocaleString('fr-FR')}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 60 }}>Base URL :</span>
                <code style={{ fontSize: 12, background: '#f1f5f9', borderRadius: 6, padding: '2px 8px', color: 'var(--text-primary)' }}>
                  {WS_BASE}/ws
                </code>
                <button onClick={async () => { await navigator.clipboard.writeText(`${WS_BASE}/ws`); toast.success('URL copiée'); }} style={btnStyle()} title="Copier l'URL de base">
                  <Copy style={{ width: 13, height: 13 }} />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
            Aucun token WS configuré. Cliquez sur « Générer » pour créer le premier token.
          </p>
        )}
      </div>

      {/* ── Endpoints table ── */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border-color)' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>Endpoints</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--text-secondary)' }}>
              Portés depuis le CRM (AntennesController.php) — auth par token WS
            </p>
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'var(--border-color)', borderRadius: 9999, padding: '2px 10px' }}>
            {API_ENDPOINTS.length} endpoints
          </span>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, width: 60 }}>Méthode</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Endpoint / Paramètres</th>
              <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Description</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, width: 110 }}>Actions</th>
              <th style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11, width: 70 }}>Statut</th>
            </tr>
          </thead>
          <tbody>
            {pagedEndpoints.map((ep) => {
              const mc = methodColor[ep.method] || { bg: '#f3f4f6', color: '#374151' };
              const params = endpointParams[ep.name] || {};
              const isPreviewActive = preview?.name === ep.name;
              return (
                <tr key={ep.name} style={{ borderBottom: '1px solid var(--border-color)', background: isPreviewActive ? '#f0fdf4' : 'transparent' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ display: 'inline-block', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, background: mc.bg, color: mc.color, fontFamily: 'monospace' }}>
                      {ep.method}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)', marginBottom: ep.params.length ? 6 : 0 }}>
                      {ep.path}
                    </div>
                    {ep.params.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {ep.params.map(p => (
                          <label key={p} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                            <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{p}:</span>
                            <input
                              value={params[p] || ''}
                              onChange={e => setParam(ep.name, p, e.target.value)}
                              placeholder={`valeur`}
                              style={{ width: 80, fontSize: 11, padding: '2px 6px', borderRadius: 4, border: '1px solid var(--border-color)', fontFamily: 'monospace' }}
                            />
                          </label>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', color: 'var(--text-primary)', fontSize: 12 }}>{ep.description}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                      {ep.doc && (
                        <button onClick={() => setDocEndpoint(ep)} style={btnStyle('#6366f1')} title="Documentation">
                          <HelpCircle style={{ width: 13, height: 13 }} />
                        </button>
                      )}
                      <button onClick={() => handleCopyUrl(ep)} style={btnStyle()} title="Copier l'URL complète">
                        <Copy style={{ width: 13, height: 13 }} />
                      </button>
                      <button onClick={() => handleTestNewTab(ep)} style={btnStyle()} title="Tester dans un nouvel onglet">
                        <ExternalLink style={{ width: 13, height: 13 }} />
                      </button>
                      <button
                        onClick={() => isPreviewActive && !preview?.loading ? setPreview(null) : handlePreview(ep)}
                        style={btnStyle(isPreviewActive ? '#059669' : undefined)}
                        title="Aperçu du résultat"
                      >
                        <Eye style={{ width: 13, height: 13 }} />
                      </button>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4, borderRadius: 9999, padding: '2px 8px', fontSize: 11, fontWeight: 500,
                      background: ep.status === 'done' ? '#ecfdf5' : '#fefce8',
                      color: ep.status === 'done' ? '#059669' : '#ca8a04',
                    }}>
                      {ep.status === 'done' ? <CheckCircle style={{ width: 11, height: 11 }} /> : <RefreshCw style={{ width: 11, height: 11 }} />}
                      {ep.status === 'done' ? 'Fait' : 'À faire'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Endpoints pagination */}
        {totalEndpointsPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: '1px solid var(--border-color)', fontSize: 13 }}>
            <span style={{ color: 'var(--text-secondary)' }}>
              {(endpointsPage - 1) * ENDPOINTS_PAGE_SIZE + 1}–{Math.min(endpointsPage * ENDPOINTS_PAGE_SIZE, API_ENDPOINTS.length)} / {API_ENDPOINTS.length}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button onClick={() => setEndpointsPage(1)} disabled={endpointsPage === 1} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: endpointsPage === 1 ? 'not-allowed' : 'pointer', opacity: endpointsPage === 1 ? 0.4 : 1, display: 'inline-flex' }}><ChevronsLeft style={{ width: 14, height: 14 }} /></button>
              <button onClick={() => setEndpointsPage(p => p - 1)} disabled={endpointsPage === 1} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: endpointsPage === 1 ? 'not-allowed' : 'pointer', opacity: endpointsPage === 1 ? 0.4 : 1, display: 'inline-flex' }}><ChevronLeft style={{ width: 14, height: 14 }} /></button>
              <span style={{ padding: '3px 10px' }}>{endpointsPage} / {totalEndpointsPages}</span>
              <button onClick={() => setEndpointsPage(p => p + 1)} disabled={endpointsPage === totalEndpointsPages} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: endpointsPage === totalEndpointsPages ? 'not-allowed' : 'pointer', opacity: endpointsPage === totalEndpointsPages ? 0.4 : 1, display: 'inline-flex' }}><ChevronRight style={{ width: 14, height: 14 }} /></button>
              <button onClick={() => setEndpointsPage(totalEndpointsPages)} disabled={endpointsPage === totalEndpointsPages} style={{ border: '1px solid var(--border-color)', background: 'transparent', borderRadius: 6, padding: '3px 6px', cursor: endpointsPage === totalEndpointsPages ? 'not-allowed' : 'pointer', opacity: endpointsPage === totalEndpointsPages ? 0.4 : 1, display: 'inline-flex' }}><ChevronsRight style={{ width: 14, height: 14 }} /></button>
            </div>
          </div>
        )}
      </div>

      {/* ── Preview panel ── */}
      {preview && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid var(--border-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Eye style={{ width: 14, height: 14, color: 'var(--primary-600)' }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                Aperçu — <code style={{ fontFamily: 'monospace', fontSize: 12 }}>{preview.name}</code>
              </span>
            </div>
            <button onClick={() => setPreview(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'inline-flex', color: 'var(--text-secondary)' }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
          <div style={{ padding: 16 }}>
            {preview.loading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
                <Loader2 className="spin-icon" style={{ width: 16, height: 16 }} /> Chargement…
              </div>
            ) : preview.error ? (
              <div style={{ padding: 12, background: '#fef2f2', borderRadius: 8, color: '#dc2626', fontSize: 13, fontFamily: 'monospace' }}>
                {preview.error}
              </div>
            ) : (
              <pre style={{
                margin: 0, padding: 12, background: '#0f172a', color: '#e2e8f0',
                borderRadius: 8, fontSize: 12, fontFamily: 'monospace',
                overflowX: 'auto', overflowY: 'auto', maxHeight: 480,
                lineHeight: 1.5,
              }}>
                {JSON.stringify(preview.data, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      {/* ── Doc modal ── */}
      {docEndpoint?.doc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setDocEndpoint(null)}>
          <div style={{ background: 'white', borderRadius: 16, width: 680, maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-color)', background: '#f8fafc' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>{docEndpoint.doc.title}</h3>
                <code style={{ fontSize: 12, color: 'var(--primary-600)' }}>{docEndpoint.method} {docEndpoint.path}</code>
              </div>
              <button onClick={() => setDocEndpoint(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'inline-flex', color: 'var(--text-secondary)' }}>
                <X style={{ width: 18, height: 18 }} />
              </button>
            </div>
            {/* Body */}
            <div style={{ overflow: 'auto', padding: '16px 20px' }}>
              {docEndpoint.doc.sections.map((section, si) => (
                <div key={si} style={{ marginBottom: 20 }}>
                  <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: 'var(--primary-600)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>{section.title}</h4>
                  {section.items && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Paramètre</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Type</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Description</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: 'var(--text-secondary)', fontSize: 11 }}>Exemple</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.items.map((item, ii) => (
                          <tr key={ii} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 600, color: '#4f46e5' }}>{item.param}</td>
                            <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: 11 }}>{item.type}</td>
                            <td style={{ padding: '6px 8px', color: 'var(--text-primary)' }}>
                              {item.desc}
                              {item.values && (
                                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {item.values.map((v, vi) => (
                                    <span key={vi} style={{ display: 'inline-block', padding: '1px 6px', borderRadius: 4, fontSize: 10, background: '#eef2ff', color: '#4338ca', fontFamily: 'monospace' }}>{v}</span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <code style={{ fontSize: 11, background: '#f1f5f9', borderRadius: 4, padding: '1px 6px', color: '#0f172a' }}>{item.example}</code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {section.extra && (
                    <div style={{ marginTop: 8, padding: 10, background: '#f8fafc', borderRadius: 8, fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.6, fontFamily: 'monospace' }}>
                      {section.extra}
                    </div>
                  )}
                  {section.examples && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {section.examples.map((ex, ei) => (
                        <div key={ei} style={{ padding: '8px 12px', background: '#0f172a', borderRadius: 8 }}>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{ex.label}</div>
                          <code style={{ fontSize: 12, color: '#67e8f9', wordBreak: 'break-all' }}>{ex.url}</code>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Generate confirm modal ── */}
      {showGenerateConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setShowGenerateConfirm(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 24, width: 400, boxShadow: '0 25px 50px rgba(0,0,0,0.15)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#ca8a04' }}>Générer un nouveau token ?</h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
              L'ancien token sera immédiatement invalidé. Tous les systèmes utilisant l'ancien token devront être mis à jour.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowGenerateConfirm(false)} style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
              <button onClick={handleGenerateToken} disabled={wsTokenGenerating} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: 'var(--primary-600)', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: wsTokenGenerating ? 0.6 : 1 }}>
                {wsTokenGenerating ? 'Génération…' : 'Générer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Parametres page ────────────────────────────── */

function Parametres() {
  const auth = useAuth();
  const [activeTab, setActiveTab] = useState('bus-events');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      setReady(true);
    }
  }, [auth.user]);

  if (!ready) return <Loading />;

  return (
    <div style={{ margin: '0 auto', padding: '0 1rem' }}>
      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Paramètres</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0' }}>Configuration et monitoring de l'application</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, borderRadius: 12, padding: 4, background: 'var(--border-color)', width: 'fit-content', marginBottom: 16 }}>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, borderRadius: 8, padding: '6px 12px',
                fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer',
                background: isActive ? 'white' : 'transparent',
                color: isActive ? 'var(--primary-600)' : 'var(--text-secondary)',
                boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                transition: 'all 0.15s ease',
              }}
            >
              <Icon style={{ width: 14, height: 14 }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'bus-events' && <BusEventsTab />}
      {activeTab === 'media' && <MediaTab />}
      {activeTab === 'ws-api' && <WsApiTab />}
    </div>
  );
}

export default Parametres;
